import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import {
  getUserDocuments,
  deleteUserDocument,
  createUserDocument,
  getUserDocumentTypes,
} from "../../services/userDocumentService.ts";
import api from "../../lib/api.ts";
import { Delete, Edit, Eye, FileText } from "lucide-react";

const SellerDocuments = () => {
  const { user } = useAuth();
  const { translate } = useLanguage();
  const [sellerDocuments, setSellerDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState({});
  const [userDocumentTypes, setUserDocumentTypes] = useState([]);

  useEffect(() => {
    const fetchUserDocumentTypes = async () => {
      const response = await getUserDocumentTypes("seller", user.userTypeId);

      setUserDocumentTypes(response);
    };
    fetchUserDocumentTypes();

    const fetchSellerDocuments = async () => {
      try {
        setLoading(true);
        const response = await getUserDocuments("seller", user.id);
        setSellerDocuments(response.data);
      } catch (error) {
        console.error("Error fetching documents:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSellerDocuments();
  }, [user.id, user.userTypeId]);

  // Helper function to get document by slug
  const getDocumentBySlug = (slug) => {
    return sellerDocuments.find(
      (doc) => doc.sellerDocumentType && doc.sellerDocumentType.slug === slug
    );
  };

  // Helper function to get file name from URL
  const getFileName = (url) => {
    if (!url) return "";
    const urlParts = url.split("/");
    const fullFileName = decodeURIComponent(urlParts[urlParts.length - 1]);

    // Truncate filename if it's too long
    if (fullFileName.length > 30) {
      const extension = fullFileName.split(".").pop();
      const nameWithoutExt = fullFileName.substring(
        0,
        fullFileName.lastIndexOf(".")
      );
      const truncatedName = nameWithoutExt.substring(0, 25) + "...";
      return `${truncatedName}.${extension}`;
    }

    return fullFileName;
  };

  // Delete document function
  const handleDeleteDocument = async (documentId) => {
    try {
      await deleteUserDocument("seller", documentId);
      // Refresh the documents list after deletion
      const response = await getUserDocuments("seller", user.id);
      setSellerDocuments(response.data);
    } catch (error) {
      console.error("Error deleting document:", error);
    }
  };

  // Upload document function
  const handleFileUpload = async (file, slug) => {
    if (!file) return;

    try {
      setUploading((prev) => ({ ...prev, [slug]: true }));
      setError(null);

      //  Check if there's an existing document for this slug and delete it
      const existingDocument = getDocumentBySlug(slug);
      if (existingDocument) {
        await deleteUserDocument("seller", existingDocument.id);
      }

      // Step 1: Upload file to S3
      const formData = new FormData();
      formData.append("images", file);
      formData.append("uploadPath", "userdocuments");

      const uploadResponse = await api.post("/image/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (uploadResponse.data) {
        // Step 2: Create document entry in database
        const documentData = {
          name: file.name,
          url: uploadResponse.data.imageUrls[0],
          slug: slug,
          userDocumentTypeId:
            getDocumentBySlug(slug)?.userDocumentType?.id ||
            getDocumentTypeIdBySlug(slug),
          userId: user.id,
        };

        await createUserDocument("seller", documentData);

        // Step 3: Refresh documents list
        const response = await getUserDocuments("seller", user.id);
        setSellerDocuments(response.data);
      }
    } catch (error) {
      console.error("Error uploading document:", error);
      setError("Failed to upload document. Please try again.");
    } finally {
      setUploading((prev) => ({ ...prev, [slug]: false }));
    }
  };

  // Helper function to get document type ID by slug (fallback)
  const getDocumentTypeIdBySlug = (slug) => {
    const staticMapping = {
      "id-front": 1,
      "id-back": 2,
      "trade-license-image": 5,
      "trn-certificate-image": 6,
    };
    return staticMapping[slug];
  };

  if (loading) {
    return (
      <div className='flex justify-center items-center h-64'>
        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600'></div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {error && (
        <div className='mb-4 p-3 text-xs sm:text-sm bg-red-50 border border-red-200 text-red-700 rounded'>
          {error}
        </div>
      )}

     <div className="grid md:grid-cols-2 grid-cols-1 gap-5">
       {/* Personal Documents Section */}
      <div className='bg-card shadow-md rounded-[8px] sm:rounded-[12px]'>
        <div className='bg-card font-medium rounded-t-[8px] sm:rounded-t-[16px] border-b border-default'>
          <div className='px-4 sm:px-5 py-3 sm:py-4 text-base sm:text-lg rounded-t-[8px] sm:rounded-t-[12px] font-semibold bg-primary-soft border-b border-black'>
            <h2 className='text-white text-sm sm:text-base md:text-lg font-semibold'>
              Personal Documents
            </h2>
          </div>
        </div>

        <div className='p-4 sm:p-6'>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6'>
            {userDocumentTypes.slice(0, 2).map((docType) => {
              const document = getDocumentBySlug(docType.slug);
              return (
                <div key={docType.slug} className='space-y-3 sm:space-y-4'>
                  <h2 className='text-white sm:text-lg text-white break-words'>
                    {docType.name}
                  </h2>

                  {/* File Upload Input - Desktop View */}
                  <input
                    type='file'
                    accept='.pdf,.doc,.docx,.jpg,.jpeg,.png'
                    className='hidden md:block w-full bg-page border border-default px-2 sm:px-3 py-2 sm:py-2.5 text-xs sm:text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent 
  text-white 
  file:mr-4 file:py-1 file:px-4
  file:rounded-full file:border-0
  file:text-xs file:font-semibold
  file:bg-primary file:text-white
  hover:file:bg-card'
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        handleFileUpload(file, docType.slug);
                      }
                    }}
                    disabled={uploading[docType.slug]}
                  />

                  {/* Custom File Upload Box - Mobile View Only */}
                  <div className="md:hidden">
                    <input
                      type='file'
                      id={`file-upload-mobile-personal-${docType.slug}`}
                      accept='.pdf,.doc,.docx,.jpg,.jpeg,.png'
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          handleFileUpload(file, docType.slug);
                        }
                      }}
                      disabled={uploading[docType.slug]}
                    />
                    <label
                      htmlFor={`file-upload-mobile-personal-${docType.slug}`}
                      className="flex items-center gap-3 px-4 py-3 border border-dashed border-default rounded-xl bg-default/20 hover:bg-default/30 transition-all cursor-pointer text-white"
                    >
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary/20 text-primary flex-shrink-0">
                        <FileText size={18} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="block text-sm font-semibold truncate text-white">
                          {document ? "Replace File" : "Choose File"}
                        </span>
                        <span className="block text-[10px] text-white/50 truncate">
                          {document ? getFileName(document.url) : "PDF, JPG, PNG up to 5MB"}
                        </span>
                      </div>
                      <span className="text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-primary hover:bg-primary/80 transition-colors text-white">
                        Upload
                      </span>
                    </label>
                  </div>

                  {uploading[docType.slug] && (
                    <div className='mt-2 text-xs sm:text-sm text-blue-600'>
                      Uploading...
                    </div>
                  )}

                  {/* Display uploaded file if exists - Desktop View */}
                  {document && (
                    <div className='hidden md:block mt-3 overflow-hidden bg-page rounded border border-default transition-all duration-200 hover:shadow-lg'>
                      <div className='flex flex-col sm:flex-row items-stretch'>
                        <div className='relative w-full h-32 shrink-0 border-r border-default overflow-hidden border-4'>
                          {document.url?.toLowerCase().includes('.pdf') ? (
                            <embed
                              src={document.url}
                              type="application/pdf"
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <img
                              className="w-full h-full object-contain transition-transform duration-500 hover:scale-110"
                              src={document.url}
                              alt="Uploaded file"
                            />
                          )}
                          <div className='absolute right-0 top-0 flex items-center gap-2 '>
                            <button
                              title="View"
                              onClick={() => window.open(document.url, "_blank")}
                              className='p-2 bg-primary text-white border border-primary rounded hover:bg-primary/10 transition-colors'
                            >
                              <Eye size={15} />
                            </button>

                            <button
                              title="Delete"
                              onClick={() => handleDeleteDocument(document.id)}
                              className='p-2 text-white bg-red-600 border border-red-500/30 rounded hover:bg-red-500/10 transition-colors ml-auto sm:ml-0'
                            >
                              <Delete size={15} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Display uploaded file if exists - Mobile View */}
                  {document && (
                    <div className="md:hidden mt-3 bg-page rounded-xl border border-default overflow-hidden">
                      {/* Image Preview Container */}
                      <div className="relative w-full h-44 bg-slate-950 flex items-center justify-center">
                        {document.url?.toLowerCase().includes('.pdf') ? (
                          <div className="flex flex-col items-center justify-center gap-2 text-white/70">
                            <FileText size={40} className="text-primary" />
                            <span className="text-xs font-medium px-2 py-1 bg-white/10 rounded">PDF Document</span>
                          </div>
                        ) : (
                          <>
                            {/* Blurred background image for premium styling */}
                            <img
                              src={document.url}
                              alt="Background Blur"
                              className="absolute inset-0 w-full h-full object-cover blur-md opacity-25"
                            />
                            <img
                              src={document.url}
                              alt="Uploaded file"
                              className="relative z-10 max-w-full max-h-full object-contain p-2 transition-transform duration-300"
                            />
                          </>
                        )}
                      </div>

                      {/* Info & Action Buttons footer bar */}
                      <div className="flex items-center justify-between p-3 bg-default/40 border-t border-default/50 gap-2">
                        <span className="text-xs text-white/60 truncate flex-1 min-w-0">
                          {getFileName(document.url)}
                        </span>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            onClick={() => window.open(document.url, "_blank")}
                            className="flex items-center gap-1 px-3 py-1.5 bg-primary text-white rounded-lg hover:bg-primary/90 text-xs font-semibold"
                          >
                            <Eye size={13} />
                            View
                          </button>
                          <button
                            onClick={() => handleDeleteDocument(document.id)}
                            className="flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-xs font-semibold"
                          >
                            <Delete size={13} />
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Business Documents Section - Only for Corporate Users */}
      {user.userTypeId === 2 && (
        <div className='bg-card shadow-md rounded-[8px] sm:rounded-[12px]'>
          <div className='bg-card font-medium rounded-t-[8px] sm:rounded-t-[16px] border-b border-default'>
            <div className='px-4 sm:px-5 py-3 sm:py-4 text-base sm:text-lg rounded-t-[8px] sm:rounded-t-[12px] font-semibold bg-primary-soft border-b border-black'>
              <h2 className='text-white text-sm sm:text-base md:text-lg font-semibold'>
                Business Documents
              </h2>
            </div>
          </div>

          <div className='p-4 sm:p-6'>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6'>
              {userDocumentTypes.slice(2, 4).map((docType) => {
                const document = getDocumentBySlug(docType.slug);
                return (
                  <div key={docType.slug} className='space-y-3 sm:space-y-4'>
                    <h2 className='text-base sm:text-lg  text-white break-words'>
                      {docType.name}
                    </h2>

                    {/* File Upload Input - Desktop View */}
                    <input
                      type='file'
                      accept='.pdf,.doc,.docx,.jpg,.jpeg,.png'
                      className='hidden md:block w-full bg-page border border-default px-2 sm:px-3 py-2 sm:py-2.5 text-xs sm:text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent 
  text-white 
  file:mr-4 file:py-1 file:px-4
  file:rounded-full file:border-0
  file:text-xs file:font-semibold
  file:bg-primary file:text-white
  hover:file:bg-card'
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          handleFileUpload(file, docType.slug);
                        }
                      }}
                      disabled={uploading[docType.slug]}
                    />

                    {/* Custom File Upload Box - Mobile View Only */}
                    <div className="md:hidden">
                      <input
                        type='file'
                        id={`file-upload-mobile-business-${docType.slug}`}
                        accept='.pdf,.doc,.docx,.jpg,.jpeg,.png'
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleFileUpload(file, docType.slug);
                          }
                        }}
                        disabled={uploading[docType.slug]}
                      />
                      <label
                        htmlFor={`file-upload-mobile-business-${docType.slug}`}
                        className="flex items-center gap-3 px-4 py-3 border border-dashed border-default rounded-xl bg-default/20 hover:bg-default/30 transition-all cursor-pointer text-white"
                      >
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary/20 text-primary flex-shrink-0">
                          <FileText size={18} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="block text-sm font-semibold truncate text-white">
                            {document ? "Replace File" : "Choose File"}
                          </span>
                          <span className="block text-[10px] text-white/50 truncate">
                            {document ? getFileName(document.url) : "PDF, JPG, PNG up to 5MB"}
                          </span>
                        </div>
                        <span className="text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-primary hover:bg-primary/80 transition-colors text-white">
                          Upload
                        </span>
                      </label>
                    </div>

                    {uploading[docType.slug] && (
                      <div className='mt-2 text-xs sm:text-sm text-blue-600'>
                        Uploading...
                      </div>
                    )}

                    {/* Display uploaded file if exists - Desktop View */}
                    {document && (
                      <div className='hidden md:block relative w-full h-32 shrink-0 border border-default rounded overflow-hidden '>
                        {document.url?.toLowerCase().includes('.pdf') ? (
                          <embed
                            src={document.url}
                            type="application/pdf"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <img
                            className="w-full h-full object-contain transition-transform duration-500 hover:scale-110"
                            src={document.url}
                            alt="Uploaded file"
                          />
                        )}

                        {/* Overlay buttons like in the info card */}
                        <div className='absolute top-0 right-0 flex gap-2'>
                          <button
                            title="View"
                            onClick={() => window.open(document.url, "_blank")}
                            className='p-2  text-xs sm:text-sm bg-primary text-white rounded hover:bg-primary-dark transition-colors font-medium'
                          >
                            <Eye size={15} />
                          </button>

                          <button
                            title="Delete"
                            onClick={() => handleDeleteDocument(document.id)}
                            className='p-2  text-xs sm:text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors font-medium'
                          >
                            <Delete size={15} />
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Display uploaded file if exists - Mobile View */}
                    {document && (
                      <div className="md:hidden mt-3 bg-page rounded-xl border border-default overflow-hidden">
                        {/* Image Preview Container */}
                        <div className="relative w-full h-44 bg-slate-950 flex items-center justify-center">
                          {document.url?.toLowerCase().includes('.pdf') ? (
                            <div className="flex flex-col items-center justify-center gap-2 text-white/70">
                              <FileText size={40} className="text-primary" />
                              <span className="text-xs font-medium px-2 py-1 bg-white/10 rounded">PDF Document</span>
                            </div>
                          ) : (
                            <>
                              {/* Blurred background image for premium styling */}
                              <img
                                src={document.url}
                                alt="Background Blur"
                                className="absolute inset-0 w-full h-full object-cover blur-md opacity-25"
                              />
                              <img
                                src={document.url}
                                alt="Uploaded file"
                                className="relative z-10 max-w-full max-h-full object-contain p-2 transition-transform duration-300"
                              />
                            </>
                          )}
                        </div>

                        {/* Info & Action Buttons footer bar */}
                        <div className="flex items-center justify-between p-3 bg-default/40 border-t border-default/50 gap-2">
                          <span className="text-xs text-white/60 truncate flex-1 min-w-0">
                            {getFileName(document.url)}
                          </span>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              onClick={() => window.open(document.url, "_blank")}
                              className="flex items-center gap-1 px-3 py-1.5 bg-primary text-white rounded-lg hover:bg-primary/90 text-xs font-semibold"
                            >
                              <Eye size={13} />
                              View
                            </button>
                            <button
                              onClick={() => handleDeleteDocument(document.id)}
                              className="flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-xs font-semibold"
                            >
                              <Delete size={13} />
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
     </div>
    </div>
  );
};

export default SellerDocuments;

